import * as api from "@modules/api";
import * as authHelper from "@modules/api/util/authHelper";
import { db } from "@modules/local/db";
import { changeLanguage } from "@modules/localizations/changeLanguage";
import { SetCurrentLanguage } from "@modules/localizations/i18n";
import { storageHelper } from "@modules/storage";
import { clearStorage } from "@modules/storage/storageHelper";
import deviceInfo from "@utils/helpers/deviceInfo";
import { thunkAuthResponseCheckerSdk } from "@utils/helpers/thunkAuthResponseCheckerSdk";
import { thunkErrorHandler } from "@utils/helpers/thunkErrorHandler";
import { thunkResponseCheckerSdk } from "@utils/helpers/thunkResponseCheckerSdk";
import { ApiMethod } from "@utils/types/common";
import { GetMyInfoInput } from "./types";

const deviceDetails = deviceInfo();

export const signIn: ApiMethod<
  api.Server.AuthenticateInput,
  api.Server.AuthenticateOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkAuthResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );
        const data = { ...body, deviceDetails };

        return client.account_authenticate_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const signUp: ApiMethod<
  api.Server.AuthenticateInput,
  api.Server.AuthenticateOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkAuthResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );
        const data = { ...body, deviceDetails };

        return client.account_register_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const signInWithGoogle: ApiMethod<
  api.Server.GoogleLoginV2Input,
  api.Server.AuthenticateOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkAuthResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );
        const data = { ...body, deviceDetails };

        return client.account_authenticate_google_post_V2(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const signInWithApple: ApiMethod<
  api.Server.AppleLoginInput,
  api.Server.AuthenticateOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkAuthResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );
        const data = { ...body, deviceDetails };

        return client.account_authenticate_apple_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const requestPhoneNumberOtp: ApiMethod<
  api.Server.RequestWhatsAppOTPInput,
  api.Server.RequestWhatsAppOTPOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );

        return client.account_requestWhatsappOtp_post_V1(body);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const signInWithPhoneNumber: ApiMethod<
  api.Server.WhatsAppLoginInput,
  api.Server.AuthenticateOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkAuthResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );
        const data = { ...body, deviceDetails };

        return client.account_authenticate_whatsapp_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const checkB2BLoginOption: ApiMethod<
  api.Server.CheckLoginOptionInput,
  api.Server.CheckLoginOptionOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );

        return client.account_checkLoginOption_post_V1(body);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const changePassword: ApiMethod<
  {
    data: api.Server.ChangeMyPasswordInput;
    token: string;
  },
  void,
  void
> = async ({ data, token }, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const apiClient = api.createApiClient({
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          apiClient.interceptors = api.publicApiClient.interceptors;

          const client = new api.Server.Server_Account_Client(
            undefined,
            apiClient
          );

          return client.account_changePassword_post_V1(data);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const requestPasswordReset: ApiMethod<
  api.Server.RequestPasswordResetThroughUserEmailInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new api.Server.Server_Account_Client(
            undefined,
            api.publicApiClient
          );

          return client.account_passwordReset_request_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const verifyPasswordResetRequest: ApiMethod<
  api.Server.VerifyPasswordResetCodeThroughUserEmailInput,
  boolean,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );

        return client.account_passwordReset_verify_post_V1(body);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const resetPassword: ApiMethod<
  api.Server.ChangePasswordWithResetCodeThroughUserEmailInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new api.Server.Server_Account_Client(
            undefined,
            api.publicApiClient
          );

          return client.account_passwordReset_reset_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const signInWithB2B: ApiMethod<
  api.Server.AuthenticateB2BInput,
  api.Server.AuthenticateOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );
        const data = { ...body, deviceDetails };

        return client.account_b2b_authenticate_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const signInWithB2BByToken: ApiMethod<
  api.Server.B2BLoginInput,
  api.Server.AuthenticateOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkAuthResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );
        const data = { ...body, deviceDetails };

        return client.account_authenticate_b2b_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const loadAuth: ApiMethod<void, api.Server.AuthenticateOutput> = async (
  variables,
  { rejectWithValue, getState }
) => {
  try {
    const response = await authHelper.loadSavedAuth();
    return response ?? {};
  } catch (error: any) {
    console.log(error);
    throw error;
  } finally {
  }
};

export const refreshToken: ApiMethod<
  api.Server.RefreshTokenV2Input,
  api.Server.AuthenticateOutput
> = async (variables, { rejectWithValue, getState }) => {
  try {
    return await authHelper.refreshToken(variables);
  } catch (error: any) {
    console.log("thunks/refreshToken", error);
    throw error;
  } finally {
  }
};

export const logout: ApiMethod<void, void> = async (
  variables,
  { rejectWithValue, getState }
) => {
  try {
    await authHelper.removeToken();
    await clearStorage();
    await db.delete({ disableAutoOpen: false });

    changeLanguage("ar", false);
  } catch (error: any) {
    console.log(error);
    throw error;
  } finally {
  }
};

// export const updateMyInfo: ApiMethod<api.UpdateProfileRequest, void> = async (
//   variables,
//   { rejectWithValue, getState },
// ) => {
//   try {
//     const client = new api.Account_Client(undefined, api.apiClient);

//     const response = await client.account_UpdateProfile(variables);

//     return;
//   } catch (error: any) {
//     console.log(error);
//     throw error;
//   } finally {
//   }
// };

export const getMyInfo: ApiMethod<
  GetMyInfoInput,
  api.Server.AccountOutput
> = async (body, { rejectWithValue, getState }) => {
  try {
    const client = new api.Server.Server_Account_Client(
      undefined,
      body?.token
        ? api.createApiClient({
            headers: {
              Authorization: `Bearer ${body?.token}`,
            },
          })
        : api.apiClient
    );

    const response = await client.account_getMyInfo_get_V1();

    const onboardingCompletedLocal = await storageHelper.getStorage("USER");

    // This condition was added to fix the wrong value of onboardingCompleted key in the
    // local storage that was set by the refresh token endpoint
    if (
      response.data?.onboardingCompleted &&
      !onboardingCompletedLocal?.onboardingCompleted
    ) {
      await storageHelper.setStorage({
        key: "USER",
        data: {
          onboardingCompleted: true,
        },
      });
    }

    return response.data ?? {};
  } catch (error: any) {
    console.log(error);
    throw error;
  } finally {
  }
};

export const setOnboardingCompleted: ApiMethod<void, void> = async (
  _,
  { rejectWithValue, dispatch }
) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new api.Server.Server_Account_Client(
            undefined,
            api.apiClient
          );

          const response = await client.account_onboardingComplteted_put_V1({});

          await storageHelper.setStorage({
            key: "USER",
            data: {
              onboardingCompleted: true,
            },
          });

          return response;
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue
  );
};

// export const refreshAccessToken: ApiMethod<
//   RefreshAccessTokenInput,
//   SignInOutput,
//   void
// > = async (data, { dispatch, rejectWithValue }) => {
//   return thunkErrorHandler(
//     async () => {
//       const response = await publicApiClient.post(
//         "api/identity/v1/Account/token/refresh",
//         data
//       );

//       const responseData = JSON.parse(response.data);

//       if (responseData.success && responseData.data) {
//         const user = {
//           token: responseData.data.token ?? "",
//           refreshToken: responseData.data.refreshToken ?? "",
//           user: {
//             id: responseData.data.id ?? "",
//             name: responseData.data.name ?? "",
//             email: responseData.data.email ?? "",
//             isVerified: responseData.data.isVerified ?? false,
//             picture: responseData.data.picture ?? "",
//             autoPlayDisabled: responseData.data.autoPlayDisabled ?? false,
//             nextTitleDisabled: responseData.data.nextTitleDisabled ?? false,
//           },
//         };

//         setLocalAuth(user);

//         return user;
//       }

//       return rejectWithValue(responseData.errors);
//     },
//     dispatch,
//     rejectWithValue
//   );
// };

// export const signInWithGoogle: ApiMethod<
//   SignInWithGoogleInput,
//   SignInWithGoogleOutput,
//   void
// > = async (data, { dispatch, rejectWithValue }) => {
//   return thunkErrorHandler(
//     async () => {
//       const response = await publicApiClient.post(
//         "api/identity/v2/Account/authenticate/google",
//         data
//       );

//       const responseData = JSON.parse(response.data);

//       if (responseData.success && responseData.data) {
//         const user = {
//           token: responseData.data.token ?? "",
//           refreshToken: responseData.data.refreshToken ?? "",
//           user: {
//             id: responseData.data.id ?? "",
//             name: responseData.data.name ?? "",
//             email: responseData.data.email ?? "",
//             isVerified: responseData.data.isVerified ?? false,
//             picture: responseData.data.picture ?? "",
//             autoPlayDisabled: responseData.data.autoPlayDisabled ?? false,
//             nextTitleDisabled: responseData.data.nextTitleDisabled ?? false,
//           },
//         };

//         setLocalAuth(user);

//         return user;
//       }

//       return rejectWithValue(responseData.errors);
//     },
//     dispatch,
//     rejectWithValue,
//     "modal"
//   );
// };

export const checkEmail: ApiMethod<string, boolean, void> = async (
  email,
  { dispatch, rejectWithValue }
) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.publicApiClient
        );

        return client.account_checkEmailExistence_get_V1(email);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const registerToBusinessGroup: ApiMethod<
  api.Server.AttachBusinessToRegisteredUserInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new api.Server.Server_Account_Client(
            undefined,
            api.publicApiClient
          );

          return client.account_register_toBusinessGroup_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const setPasswordByInvitationToken: ApiMethod<
  api.Server.SetUserPasswordByTokenInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new api.Server.Server_Account_Client(
            undefined,
            api.publicApiClient
          );

          return client.account_setPasswordByToken_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const getLinkedAccounts: ApiMethod<
  void,
  api.Server.AppResponseOfListOfAuthProviderOutput
> = async (_, { rejectWithValue, getState }) => {
  try {
    const client = new api.Server.Server_Account_Client(
      undefined,
      api.apiClient
    );

    const response = await client.account_authProviders_get_V1();

    return response ?? {};
  } catch (error: any) {
    console.log(error);
    throw error;
  } finally {
  }
};

export const updateUsername: ApiMethod<
  api.Server.UpdateCurrentUserNameInput,
  void,
  api.Server.AppResponseOfAccountOutput
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_updateName_put_V1(body);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const changeEmail: ApiMethod<
  api.Server.ChangeEmailInput,
  api.Server.AppResponse,
  void
> = async (input, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_changeEmail_post_V1(input);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const linkGoogle: ApiMethod<
  api.Server.LinkGoogleLoginInput,
  api.Server.AppResponse,
  void
> = async (input, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_link_google_post_V2(input);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const linkApple: ApiMethod<
  api.Server.LinkAppleLoginInput,
  api.Server.AppResponse,
  void
> = async (input, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_link_apple_post_V1(input);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const requestDeleteAccount: ApiMethod<
  void,
  api.Server.AppResponse,
  void
> = async (_, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_requestDelete_delete_V1();
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const setMyEmailPassword: ApiMethod<
  api.Server.SetMyEmailAndPasswordInput,
  api.Server.AppResponse,
  void
> = async (input, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_setMyEmailPassword_post_V1(input);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const changMyPassword: ApiMethod<
  api.Server.ChangeMyPasswordInput,
  api.Server.AppResponse,
  void
> = async (input, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_changePassword_post_V1(input);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const checkPhoneNumber: ApiMethod<
  string,
  api.Server.AppResponse,
  void
> = async (input, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new api.Server.Server_Account_Client(
          undefined,
          api.apiClient
        );

        return client.account_checkPhoneNumber_get_V1(input);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};
